const TILE_SIZE = 256;

// Matches DisplayMap's defaultZoom, which is the closest the map ever opens at.
const MAX_ZOOM = 15;
const MIN_ZOOM = 1;

// google.maps fitBounds keeps 45px of padding around the bounds unless told otherwise.
// Reproducing it here is what keeps the static preview and the dynamic map framed alike.
const FIT_BOUNDS_PADDING = 45;

// The Maps Static API rejects any edge longer than 640.
const STATIC_MAP_MAX_EDGE = 640;

const COORDINATE_PRECISION = 5;

const toMercatorY = (latitude) => {
  const sine = Math.sin((latitude * Math.PI) / 180);
  const clampedSine = Math.min(Math.max(sine, -0.9999), 0.9999);

  return 0.5 - Math.log((1 + clampedSine) / (1 - clampedSine)) / (4 * Math.PI);
};

const fromMercatorY = (mercatorY) =>
  (180 / Math.PI) * Math.atan(Math.sinh(Math.PI * (1 - 2 * mercatorY)));

const round = (value) => Number(value.toFixed(COORDINATE_PRECISION));

const getBounds = (markers) =>
  markers.reduce(
    (bounds, marker) => ({
      north: Math.max(bounds.north, marker.latitude),
      south: Math.min(bounds.south, marker.latitude),
      east: Math.max(bounds.east, marker.longitude),
      west: Math.min(bounds.west, marker.longitude),
    }),
    {
      north: -90,
      south: 90,
      east: -180,
      west: 180,
    },
  );

const getFitZoom = ({ bounds, width, height }) => {
  const latitudeFraction = Math.abs(toMercatorY(bounds.north) - toMercatorY(bounds.south));
  const longitudeFraction = (bounds.east - bounds.west) / 360;

  const zoomForLatitude =
    latitudeFraction > 0 ? Math.log2(height / TILE_SIZE / latitudeFraction) : MAX_ZOOM;
  const zoomForLongitude =
    longitudeFraction > 0 ? Math.log2(width / TILE_SIZE / longitudeFraction) : MAX_ZOOM;

  const fitZoom = Math.floor(Math.min(zoomForLatitude, zoomForLongitude));

  return Math.min(Math.max(fitZoom, MIN_ZOOM), MAX_ZOOM);
};

/**
 * Static map request that covers exactly the same ground as the dynamic map would.
 *
 * Halving the requested size while dropping one zoom level leaves the covered area
 * unchanged, so oversized containers stay framed correctly instead of being stretched.
 * Paired with scale=2 the image still comes back at one device pixel per css pixel.
 */
export const getStaticMapRequest = ({ center, zoom, width, height }) => {
  let downscaleSteps = 0;
  while (Math.max(width, height) / 2 ** downscaleSteps > STATIC_MAP_MAX_EDGE) {
    downscaleSteps += 1;
  }

  return {
    center,
    zoom: Math.max(zoom - downscaleSteps, 0),
    width: Math.round(width / 2 ** downscaleSteps),
    height: Math.round(height / 2 ** downscaleSteps),
    scale: 2,
  };
};

/**
 * Center and zoom that frame every marker inside a container of the given size.
 *
 * Both the static preview and the dynamic map read their viewport from here, which is
 * what lets one swap in for the other with no visible jump. Coordinates are rounded so
 * that an incidental re-render does not produce a new static map url, and a new url is
 * a new billed request.
 *
 * @returns {{ center: { lat: number, lng: number }, zoom: number } | null} Null when
 *   there is nothing to frame or the container has not been measured yet.
 */
const getMapViewport = ({ markers, width, height }) => {
  if (!markers || !markers.length || !width || !height) {
    return null;
  }

  const [firstMarker] = markers;

  // DisplayMap skips fitBounds for a lone marker and falls back to its default zoom.
  if (markers.length < 2) {
    return {
      center: { lat: round(firstMarker.latitude), lng: round(firstMarker.longitude) },
      zoom: MAX_ZOOM,
    };
  }

  const bounds = getBounds(markers);

  return {
    center: {
      lat: round(fromMercatorY((toMercatorY(bounds.north) + toMercatorY(bounds.south)) / 2)),
      lng: round((bounds.east + bounds.west) / 2),
    },
    zoom: getFitZoom({
      bounds,
      width: Math.max(width - FIT_BOUNDS_PADDING * 2, 1),
      height: Math.max(height - FIT_BOUNDS_PADDING * 2, 1),
    }),
  };
};

export default getMapViewport;
